import { useState, useEffect } from 'react';

const getInitialDeviceState = () => {
  if (typeof window === 'undefined') {
    return {
      width: 1024,
      height: 768,
      isMobile: false,
      isTablet: false,
      orientation: 'portrait',
    };
  }

  const width = window.innerWidth;
  const height = window.innerHeight;
  return {
    width,
    height,
    isMobile: width <= 768,
    isTablet: width > 768 && width <= 1024,
    orientation: width > height ? 'landscape' : 'portrait',
  };
};

export const useMobile = () => {
  const initialState = getInitialDeviceState();
  const [isMobile, setIsMobile] = useState(initialState.isMobile);
  const [isTablet, setIsTablet] = useState(initialState.isTablet);
  const [orientation, setOrientation] = useState(initialState.orientation);
  const [viewportHeight, setViewportHeight] = useState(initialState.height);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      setOrientation(width > height ? 'landscape' : 'portrait');
      setViewportHeight(height);
    };

    const handleResize = () => {
      checkDevice();
    };

    const handleOrientationChange = () => {
      // Small delay to get accurate dimensions after orientation change
      setTimeout(checkDevice, 100);
    };

    checkDevice();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return {
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    orientation,
    viewportHeight,
    isLandscape: orientation === 'landscape',
    isPortrait: orientation === 'portrait',
  };
};

export const useKeyboard = () => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [initialViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      
      // Debounce resize events
      resizeTimeout = setTimeout(() => {
        const currentHeight = window.innerHeight;
        const heightDifference = initialViewportHeight - currentHeight;
        
        // If viewport height decreased significantly, assume keyboard is open
        const keyboardThreshold = 150; // 150px threshold
        const keyboardIsOpen = heightDifference > keyboardThreshold;
        
        setIsKeyboardOpen(keyboardIsOpen);
        setKeyboardHeight(keyboardIsOpen ? heightDifference : 0);
        
        // Update CSS custom property for keyboard height
        document.documentElement.style.setProperty(
          '--keyboard-height', 
          keyboardIsOpen ? `${heightDifference}px` : '0px'
        );
      }, 150);
    };

    const handleFocus = (e) => {
      // Check if focused element is an input
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName);
      if (isInput) {
        // Small delay to allow keyboard to appear
        setTimeout(handleResize, 300);
      }
    };

    const handleBlur = () => {
      // Small delay to allow keyboard to disappear
      setTimeout(handleResize, 300);
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    // Initial check
    handleResize();

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      document.documentElement.style.removeProperty('--keyboard-height');
    };
  }, [initialViewportHeight]);

  return {
    isKeyboardOpen,
    keyboardHeight,
    adjustedViewportHeight: window.innerHeight,
  };
};

export const useTouch = () => {
  const [touchSupported, setTouchSupported] = useState(false);
  const [gestureSupported, setGestureSupported] = useState(false);

  useEffect(() => {
    const checkTouch = () => {
      setTouchSupported('ontouchstart' in window || navigator.maxTouchPoints > 0);
      setGestureSupported('ongesturestart' in window);
    };

    checkTouch();
  }, []);

  const addTouchFeedback = (element) => {
    if (!element || !touchSupported) return;

    const handleTouchStart = () => {
      element.classList.add('touch-feedback-active');
    };

    const handleTouchEnd = () => {
      element.classList.remove('touch-feedback-active');
    };

    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchEnd);
    };
  };

  const createSwipeHandler = (onSwipeLeft, onSwipeRight, threshold = 50) => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const distance = touchEndX - touchStartX;
      
      if (Math.abs(distance) > threshold) {
        if (distance > 0 && onSwipeRight) {
          onSwipeRight();
        } else if (distance < 0 && onSwipeLeft) {
          onSwipeLeft();
        }
      }
    };

    return {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
    };
  };

  return {
    touchSupported,
    gestureSupported,
    addTouchFeedback,
    createSwipeHandler,
  };
};

export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState('unknown');

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    const updateConnectionType = () => {
      if ('connection' in navigator) {
        setConnectionType(navigator.connection.effectiveType || 'unknown');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', updateConnectionType);
      updateConnectionType();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if ('connection' in navigator) {
        navigator.connection.removeEventListener('change', updateConnectionType);
      }
    };
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    connectionType
    // isSlowConnection: ['slow-2g', '2g'].includes(connectionType),
  };
};


const mobileHooks = {
  useMobile,
  useKeyboard,
  useTouch,
  useNetworkStatus,
};

export default mobileHooks;
